import { Component } from 'preact';
import { VISIBILITY_TILES } from '../../utils/arena/gameModes';
import { isPracticeBotAvailable } from '../../utils/updateChannel';
import { isPhoneLayout } from '../../utils/platform';
import { playUI, UI } from '../../utils/arena/uiSounds';
import * as lfg from '../../utils/arena/lfgStore';
import LfgWidget from '../LfgWidget';
import { TEXT_PRIMARY, TEXT_MUTED } from '../../lib/medievalTheme';
import { t } from '../../localization/t';

// Simple inline SVG glyphs. The right-side browser is the star of
// the SessionBrowser layout; this menu anchors it, not the other
// way around, so the buttons stay visually calm.
const ICONS = {
  solo: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{ width: '1em', height: '1em' }}>
      <circle cx="12" cy="7" r="4" />
      <path d="M4 22v-1a6 6 0 0 1 6-6h4a6 6 0 0 1 6 6v1" />
    </svg>
  ),
  bot: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{ width: '1em', height: '1em' }}>
      <rect x="5" y="8" width="14" height="11" rx="2" />
      <path d="M12 8V5" />
      <circle cx="12" cy="3.5" r="1.5" />
      <path d="M9 13h.01M15 13h.01" />
      <path d="M9.5 16.5h5" />
    </svg>
  ),
  friends: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{ width: '1em', height: '1em' }}>
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  ),
  public: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{ width: '1em', height: '1em' }}>
      <circle cx="12" cy="12" r="10" />
      <path d="M2 12h20" />
      <path d="M12 2a15 15 0 0 1 0 20M12 2a15 15 0 0 0 0 20" />
    </svg>
  ),
};

class VisibilityButton extends Component {
  handleClick = () => {
    playUI(UI.CONFIRM);
    this.props.onClick?.();
  };

  render() {
    const { tile } = this.props;
    return (
      <button
        type="button"
        data-tutorial={`session-visibility-${tile.key}`}
        onClick={this.handleClick}
        className="vh-visibility-btn"
      >
        {/* Real blurred image copy. Guaranteed to render because
            `filter: blur()` is a pixel filter that doesn't depend
            on the flaky backdrop-filter path. `background-
            attachment: fixed` aligns the image to the viewport so
            each button shows the portion of the fullscreen play-bg
            that's behind it. True frosted glass. */}
        <div
          aria-hidden
          style={{
            position: 'absolute',
            inset: 0,
            backgroundImage: `url("/play-bg.webp")`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            transform: 'scale(1.18)',
            transformOrigin: 'center',
            filter: 'blur(22px) brightness(0.45) saturate(1.1)',
            pointerEvents: 'none',
            zIndex: 0,
          }}
        />
        <div
          aria-hidden
          style={{
            position: 'absolute',
            inset: 0,
            background: `url("/tex-noise-panel.webp"), linear-gradient(180deg, rgba(18, 14, 8, 0.80) 0%, rgba(10, 8, 6, 0.84) 100%)`,
            backgroundBlendMode: 'soft-light, normal',
            pointerEvents: 'none',
            zIndex: 1,
          }}
        />
        <div className="vh-visibility-icon" style={{ position: 'relative', zIndex: 2 }}>
          {ICONS[tile.key] || null}
        </div>
        <div className="min-w-0 flex-1" style={{ position: 'relative', zIndex: 2 }}>
          <div
            className="arena-heading text-lg font-bold"
            style={{
              color: TEXT_PRIMARY,
              textShadow: '0 -1px 0 rgba(0,0,0,0.7), 0 1px 0 rgba(255,220,140,0.12), 0 2px 4px rgba(0,0,0,0.6)',
            }}
          >
            {t(tile.title)}
          </div>
          <div className="text-[0.6875cqw] italic" style={{ color: TEXT_MUTED }}>
            {t(tile.tagline)}
          </div>
        </div>
      </button>
    );
  }
}

// Vertical stack of visibility buttons (Solo / Friends / Public,
// plus the dev-only vs Bot) rendered on the left column of
// SessionBrowser. Clicking a button advances to the mode picker
// sub-screen filtered by that visibility.
//
// The practice-bot tile shows only where the server admits bot matches
// (a dev build on the local server, or the beta channel). The server
// refuses it independently. This only keeps it out of the UI.
// Matchmaking sits first as the same live lamp plate the Hall header
// carries, so the Play menu always shows whether the player is queued.
// Not a visibility (never reaches the mode picker); the app opens the
// looking-for-a-match panel for the 'queue' key. The panel is
// desktop-sized, so phones keep the manual tiles only.
export default class VisibilityStrip extends Component {
  state = { lfg: lfg.getState() };
  componentDidMount() { this._unsubLfg = lfg.subscribe((st) => this.setState({ lfg: st })); }
  componentWillUnmount() { this._unsubLfg?.(); }

  render() {
    const { onPick } = this.props;
    return (
      <div className="flex flex-col gap-3">
        {!isPhoneLayout() ? (
          <LfgWidget
            lfg={this.state.lfg}
            width="100%"
            height="4.5cqw"
            radius="var(--radius-lg)"
            lampSize="2cqw"
            fontSize="1.15cqw"
            idleLabel={t('Find a match')}
            dataTutorial="session-visibility-queue"
            onClick={() => { playUI(UI.OPEN); onPick?.('queue'); }}
          />
        ) : null}
        {VISIBILITY_TILES.filter((tile) => !tile.practiceBot || isPracticeBotAvailable()).map((tile) => (
          <VisibilityButton
            key={tile.key}
            tile={tile}
            onClick={() => onPick?.(tile.key)}
          />
        ))}
      </div>
    );
  }
}
